import { type ReactNode, useState } from 'react';
import { ArrowLeftRight } from 'lucide-react';
import type { AuthUser } from '@/types/auth';
import { useSwapIg } from '@/hooks/use-ig-mutations';
import { ProfileSection } from './profile-section';
import { IgHandleRow } from './ig-handle-row';
import { IgReplaceModal, type ReplaceMode } from './ig-replace-modal';

const SECTION_DESC =
  'Tu IG principal es la cuenta que ManyChat usa para reconocerte. El secundario puede ser tu cuenta personal.';
const SWAP_CLS =
  'flex w-full items-center justify-center gap-xs rounded-md border border-border-subtle px-sm py-sm text-xs text-text-secondary transition-colors hover:bg-surface-elevated hover:text-text-primary disabled:opacity-50';

function SwapButton({
  onClick,
  pending,
}: {
  onClick: () => void;
  pending: boolean;
}): ReactNode {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={pending}
      className={SWAP_CLS}
    >
      <ArrowLeftRight size={12} />
      {pending ? 'Intercambiando...' : 'Intercambiar principal y secundario'}
    </button>
  );
}

function IgRows({
  user,
  setMode,
}: {
  user: AuthUser;
  setMode: (m: ReplaceMode) => void;
}): ReactNode {
  return (
    <>
      <IgHandleRow
        label="Principal"
        value={user.ig_username}
        onEdit={() => setMode('replace_primary')}
      />
      <IgHandleRow
        label="Secundario"
        value={user.personal_ig_username}
        onEdit={() => setMode('replace_secondary')}
      />
    </>
  );
}

export function IgHandlesSection({ user }: { user: AuthUser }): ReactNode {
  const [mode, setMode] = useState<ReplaceMode | null>(null);
  const swap = useSwapIg();
  const onSwap = (): void => {
    if (window.confirm('Intercambiar tu IG principal con el secundario?')) {
      swap.mutate({ id: user.id, mode: 'swap' });
    }
  };
  return (
    <ProfileSection title="Cuentas de Instagram" description={SECTION_DESC}>
      <IgRows user={user} setMode={setMode} />
      {!!user.personal_ig_username && (
        <SwapButton onClick={onSwap} pending={swap.isPending} />
      )}
      <IgReplaceModal
        open={mode !== null}
        mode={mode}
        memberId={user.id}
        currentPrimary={user.ig_username}
        onClose={() => setMode(null)}
      />
    </ProfileSection>
  );
}
